// src/components/FinishLynx/PhotoFinishUploader.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Snackbar,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Upload as UploadIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as CloudUploadIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const PhotoFinishUploader = ({ onFileUpload, onImportComplete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estado para archivos
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'list' o 'grid'
  
  // Estado para el diálogo de vista previa
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Estado para snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter(file => 
      file.type.startsWith('image/') || 
      file.name.toLowerCase().endsWith('.jpg') || 
      file.name.toLowerCase().endsWith('.png') || 
      file.name.toLowerCase().endsWith('.jpeg')
    );
    
    // Extender los archivos con URLs para la vista previa
    const filesWithPreviews = imageFiles.map(file => ({
      file,
      id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      preview: URL.createObjectURL(file)
    }));
    
    setSelectedFiles(prev => [...prev, ...filesWithPreviews]);
  };
  
  // Limpiar las URLs de objeto cuando se desmonte el componente
  useEffect(() => {
    return () => {
      // Revocar object URLs para evitar fugas de memoria
      selectedFiles.forEach(fileObj => {
        URL.revokeObjectURL(fileObj.preview);
      });
    };
  }, [selectedFiles]);
  
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    
    // Inicializar estado de progreso para cada archivo
    const initialProgress = {};
    const initialStatus = {};
    selectedFiles.forEach(fileObj => {
      initialProgress[fileObj.id] = 0;
      initialStatus[fileObj.id] = 'uploading';
    });
    
    setUploadProgress(initialProgress);
    setUploadStatus(initialStatus);
    
    try {
      // Simular carga uno por uno con tiempos diferentes
      for (const fileObj of selectedFiles) {
        // Simular carga
        await new Promise(resolve => {
          let progress = 0;
          const interval = setInterval(() => {
            progress += Math.random() * 15;
            if (progress >= 100) {
              progress = 100;
              clearInterval(interval);
              resolve();
            }
            
            setUploadProgress(prev => ({
              ...prev,
              [fileObj.id]: Math.min(progress, 100)
            }));
          }, 300);
        });
        
        // Simular resultado aleatorio (90% de éxito)
        const success = Math.random() > 0.1;
        
        setUploadStatus(prev => ({
          ...prev,
          [fileObj.id]: success ? 'success' : 'error'
        }));
        
        // Pequeña pausa entre archivos
        await new Promise(r => setTimeout(r, 300));
      }
      
      // Llamar a la función de callback proporcionada por el padre (si existe)
      if (onFileUpload) {
        const rawFiles = selectedFiles.map(fileObj => fileObj.file);
        await onFileUpload(rawFiles, 'photofinish');
      }
      
      // Notificar que se ha completado la importación
      if (onImportComplete) {
        onImportComplete({
          success: true,
          message: `${selectedFiles.length} imágenes importadas correctamente`,
          files: selectedFiles
        });
      }
      
      // Mostrar mensaje de éxito
      const successCount = Object.values(uploadStatus).filter(status => status === 'success').length;
      const errorCount = Object.values(uploadStatus).filter(status => status === 'error').length;
      
      let message = '';
      let severity = 'success';
      
      if (errorCount === 0) {
        message = `${successCount} imágenes subidas correctamente`;
      } else if (successCount === 0) {
        message = 'Error al subir las imágenes';
        severity = 'error';
      } else {
        message = `${successCount} imágenes subidas, ${errorCount} con errores`;
        severity = 'warning';
      }
      
      setSnackbar({
        open: true,
        message,
        severity
      });
      
      // No limpiamos los archivos para mostrar el estado final
      // setSelectedFiles([]);
    } catch (error) {
      console.error('Error subiendo imágenes:', error);
      setSnackbar({
        open: true,
        message: 'Error al subir las imágenes. Inténtalo de nuevo.',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };
  
  const handleRemoveFile = (id) => {
    setSelectedFiles(prev => {
      // Encontrar el objeto de archivo para revocar su URL
      const fileToRemove = prev.find(f => f.id === id);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      
      // Filtrar la lista
      return prev.filter(f => f.id !== id);
    });
  };
  
  const handlePreviewImage = (preview) => {
    setPreviewImage(preview);
    setPreviewOpen(true);
  };
  
  const handleClosePreview = () => {
    setPreviewOpen(false);
  };
  
  const toggleViewMode = () => {
    setViewMode(prev => prev === 'list' ? 'grid' : 'list');
  };
  
  // Renderizado de vista de lista
  const renderListView = () => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">
          {selectedFiles.length} imagen(es) seleccionada(s)
        </Typography>
        <Button 
          size="small" 
          onClick={toggleViewMode}
          startIcon={<ViewIcon />}
        >
          Ver como cuadrícula
        </Button>
      </Box>
      
      <List dense>
        {selectedFiles.map((fileObj) => {
          const { id, file, preview } = fileObj;
          const progress = uploadProgress[id] || 0;
          const status = uploadStatus[id] || 'pending';
          
          return (
            <ListItem
              key={id}
              secondaryAction={
                !uploading ? (
                  <IconButton 
                    edge="end" 
                    aria-label="delete" 
                    onClick={() => handleRemoveFile(id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                ) : (
                  status === 'uploading' ? (
                    <CircularProgress 
                      size={24} 
                      variant="determinate" 
                      value={progress} 
                    />
                  ) : status === 'success' ? (
                    <CheckIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )
                )
              }
            >
              <ListItemIcon onClick={() => handlePreviewImage(preview)} sx={{ cursor: 'pointer' }}>
                <ImageIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary={file.name} 
                secondary={`${(file.size / 1024).toFixed(2)} KB`}
              />
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
  
  // Renderizado de vista de cuadrícula
  const renderGridView = () => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">
          {selectedFiles.length} imagen(es) seleccionada(s)
        </Typography>
        <Button 
          size="small" 
          onClick={toggleViewMode}
          startIcon={<ViewIcon />}
        >
          Ver como lista
        </Button>
      </Box>
      
      <Grid container spacing={2}>
        {selectedFiles.map((fileObj) => {
          const { id, file, preview } = fileObj;
          const progress = uploadProgress[id] || 0;
          const status = uploadStatus[id] || 'pending';
          
          return (
            <Grid item xs={6} sm={4} md={3} key={id}>
              <Card variant="outlined">
                <CardMedia
                  component="img"
                  height="140"
                  image={preview}
                  alt={file.name}
                  onClick={() => handlePreviewImage(preview)}
                  sx={{ cursor: 'pointer', objectFit: 'cover' }}
                />
                <CardContent sx={{ p: 1 }}>
                  <Typography variant="body2" noWrap title={file.name}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${(file.size / 1024).toFixed(2)} KB`}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 1, pt: 0, justifyContent: 'space-between' }}>
                  {!uploading ? (
                    <IconButton 
                      size="small" 
                      onClick={() => handleRemoveFile(id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    status === 'uploading' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress 
                          size={20} 
                          variant="determinate" 
                          value={progress} 
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          {Math.round(progress)}%
                        </Typography>
                      </Box>
                    ) : status === 'success' ? (
                      <Tooltip title="Subida exitosa">
                        <CheckIcon color="success" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Error al subir">
                        <ErrorIcon color="error" />
                      </Tooltip>
                    )
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
  
  return (
    <Box>
      <Typography variant="body1" gutterBottom>
        Sube imágenes de fotofinish para visualización y análisis
      </Typography>
      
      <Box sx={{ mb: 2, mt: 2 }}>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id="photo-finish-upload"
          type="file"
          multiple
          onChange={handleFileSelect}
        />
        <label htmlFor="photo-finish-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<UploadIcon />}
            disabled={uploading}
          >
            Seleccionar imágenes
          </Button>
        </label>
      </Box>
      
      {selectedFiles.length > 0 && (
        <>
          {viewMode === 'list' ? renderListView() : renderGridView()}
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            >
              {uploading ? 'Subiendo...' : 'Subir imágenes'}
            </Button>
          </Box>
        </>
      )}
      
      {/* Diálogo de vista previa */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Vista previa</DialogTitle>
        <DialogContent>
          {previewImage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
              <img 
                src={previewImage} 
                alt="Vista previa" 
                style={{ maxWidth: '100%', maxHeight: '70vh' }} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PhotoFinishUploader;